import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { store } from './store';

// 추가 확장 프로그램 에러 차단 (React 앱 레벨)
const blockExtensionErrors = () => {
  // /// console.error 오버라이드 (크롬 확장 프로그램 에러 숨기기)
  const originalConsoleError = console.error;
  
  // 안전하게 문자열로 변환하는 함수 (순환 참조 방지)
  const safeStringify = (arg: any): string => {
    if (arg === null || arg === undefined) {
      return String(arg);
    }
    if (typeof arg === 'string') {
      return arg;
    }
    if (typeof arg === 'number' || typeof arg === 'boolean') {
      return String(arg);
    }
    try {
      return JSON.stringify(arg);
    } catch (e) {
      try {
        return String(arg);
      } catch {
        return '[Circular]';
      }
    }
  };
  
  console.error = function(...args) {
    try {
      const errorStr = args.map(safeStringify).join(' ');
      if (errorStr.includes('chrome-extension://') || 
          errorStr.includes('Cannot read properties of null (reading \'type\')') ||
          errorStr.includes('Extension context invalidated') ||
          errorStr.includes('client_process') ||
          errorStr.includes('DataCloneError') ||
          errorStr.includes('could not be cloned') ||
          errorStr.includes('extension-blocker.js') ||
          errorStr.includes('Unexpected token \'<\'') ||
          errorStr.includes('moz-extension://') ||
          errorStr.includes('safari-extension://')) {
        return; // 확장 프로그램 및 본인인증 관련 에러는 출력하지 않음
      }
      return originalConsoleError.apply(console, args);
    } catch (e) {
      // 순환 참조 등으로 인한 에러는 무시하고 원본 함수 호출
      return originalConsoleError.apply(console, args);
    }
  };

  // /// console.warn 오버라이드
  const originalConsoleWarn = console.warn;
  console.warn = function(...args) {
    try {
      const warnStr = args.map(safeStringify).join(' ');
      if (warnStr.includes('styled-components: it looks like an unknown prop')) {
        return; // styled-components prop 경고 숨기기
      }
      return originalConsoleWarn.apply(console, args);
    } catch (e) {
      // 순환 참조 등으로 인한 에러는 무시하고 원본 함수 호출
      return originalConsoleWarn.apply(console, args);
    }
  };

  // React 에러 경계를 우회하는 확장 프로그램 에러들을 위한 추가 처리
  const handleGlobalError = (event: Event) => {
    const errorEvent = event as ErrorEvent;
    const error = errorEvent.error || errorEvent;
    const message = errorEvent.message || error?.message || '';
    const filename = errorEvent.filename || '';
    
    if (filename.includes('chrome-extension://') || 
        message.includes('Cannot read properties of null (reading \'type\')') ||
        message.includes('Extension context invalidated') ||
        message.includes('client_process') ||
        message.includes('DataCloneError') ||
        filename.includes('inpage.js') ||
        filename.includes('666666') ||
        message.includes('net::ERR_NAME_NOT_RESOLVED') ||
        filename.includes('extension-blocker.js')) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  };

  const handleUnhandledRejection = (event: Event) => {
    const rejectionEvent = event as PromiseRejectionEvent;
    const reason = rejectionEvent.reason;
    const reasonStr = reason?.toString() || '';
    
    if (reasonStr.includes('chrome-extension://') || 
        reasonStr.includes('Cannot read properties of null (reading \'type\')') ||
        reasonStr.includes('Extension context invalidated') ||
        reasonStr.includes('client_process') ||
        reasonStr.includes('DataCloneError') ||
        reasonStr.includes('via.placeholder.com') ||
        reasonStr.includes('picsum.photos') ||
        reasonStr.includes('net::ERR_NAME_NOT_RESOLVED')) {
      event.preventDefault();
      return;
    }
  };

  // 이벤트 리스너 등록 (타입 안전하게)
  document.addEventListener('error', handleGlobalError, true);
  window.addEventListener('error', handleGlobalError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

  // React 에러 바운더리도 확장 프로그램 에러 무시하도록 전역 플래그 설정
  (window as any).__IGNORE_EXTENSION_ERRORS__ = true;
  (window as any).__SUPPRESS_MOBILE_AUTH_ERRORS__ = true;
};

// 확장 프로그램 에러 차단 실행
blockExtensionErrors();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// React 렌더링 전에 추가 안전장치
try {
  root.render(
      <ErrorBoundary>
        <Provider store={store}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <App />
          </BrowserRouter>
        </Provider>
      </ErrorBoundary>
  );
} catch (error) {
  // 렌더링 중 확장 프로그램 에러 발생 시 무시
  const errorStr = error?.toString() || '';
  if (errorStr.includes('chrome-extension://') || 
      errorStr.includes('Cannot read properties of null (reading \'type\')')) {
    /// console.warn('Extension error during render ignored:', error);
    // 에러 무시하고 재시도
    setTimeout(() => {
      root.render(
          <ErrorBoundary>
            <Provider store={store}>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <App />
              </BrowserRouter>
            </Provider>
          </ErrorBoundary>
      );
    }, 100);
  } else {
    /// console.error('Actual render error:', error);
    throw error;
  }
}
